import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database, Plus, Loader2, Trash2, Pencil, X, Plug, RefreshCw, Search, ListTree,
  ScrollText, CheckCircle2, AlertTriangle, ShieldCheck, Upload, TableProperties, Server, Cloud, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";

const KIND_META = {
  fusion: { icon: Cloud, color: "text-rose-500", bg: "bg-rose-500/10" },
  netsuite: { icon: Cloud, color: "text-orange-500", bg: "bg-orange-500/10" },
  oracle_db: { icon: Server, color: "text-violet", bg: "bg-violet/10" },
  sftp: { icon: FileSpreadsheet, color: "text-emerald-600", bg: "bg-emerald-500/10" },
};
const STATUS_META = {
  nueva: { l: "Nueva", c: "text-navy/50 bg-navy/5" },
  sandbox: { l: "Sandbox", c: "text-gold bg-gold/15" },
  conectada: { l: "Conectada", c: "text-emerald-600 bg-emerald-500/10" },
  error: { l: "Error", c: "text-rose-500 bg-rose-500/10" },
};

const KpiCard = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-wide text-navy/40">{label}</div>
    <div className="text-3xl font-semibold text-navy mt-1">{value}</div>
  </div>
);

export default function OracleHub() {
  const [conns, setConns] = useState([]);
  const [ov, setOv] = useState({ connections: 0, active: 0, records: 0, entities: 0 });
  const [kinds, setKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [editModal, setEditModal] = useState(null); // {conn} or {new:true}
  const [discoverModal, setDiscoverModal] = useState(null); // {conn, objects, selected}
  const [dataModal, setDataModal] = useState(null); // {conn, datasets, active, table}
  const [logsModal, setLogsModal] = useState(null); // {conn, logs}
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/oracle/connections"), api.get("/oracle/overview"), api.get("/oracle/kinds")])
      .then(([c, o, k]) => { setConns(c.data); setOv(o.data); setKinds(k.data.kinds); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async (conn) => {
    setBusyId(conn.id);
    try {
      const { data } = await api.post(`/oracle/connections/${conn.id}/test`);
      data.ok ? toast.success(data.detail) : toast.error(data.detail);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo probar"); }
    finally { setBusyId(""); }
  };

  const openDiscover = async (conn) => {
    setBusyId(conn.id);
    try {
      const { data } = await api.get(`/oracle/connections/${conn.id}/discover`);
      setDiscoverModal({ conn, objects: data.objects, sandbox: data.sandbox, selected: new Set(data.objects.map((o) => o.name)) });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descubrir"); }
    finally { setBusyId(""); }
  };

  const runSync = async (conn, entities) => {
    setBusyId(conn.id);
    try {
      const { data } = await api.post(`/oracle/connections/${conn.id}/sync`, { entities: entities || [] });
      if (data.status === "ok") toast.success(`Sincronización completa · ${data.total} registros absorbidos`);
      else toast.error(`Sincronización con error: ${data.error || ""}`);
      setDiscoverModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar"); }
    finally { setBusyId(""); }
  };

  const openData = async (conn) => {
    try {
      const { data } = await api.get(`/oracle/connections/${conn.id}/datasets`);
      setDataModal({ conn, datasets: data, active: null, table: null });
      if (data.length) loadTable(conn, data[0].entity, data);
    } catch (e) { toast.error("No se pudieron cargar los datos"); }
  };
  const loadTable = async (conn, entity, datasets) => {
    try {
      const { data } = await api.get(`/oracle/connections/${conn.id}/datasets/${encodeURIComponent(entity)}?limit=100`);
      setDataModal((m) => ({ ...(m || { conn, datasets }), conn, datasets: datasets || m.datasets, active: entity, table: data }));
    } catch { toast.error("No se pudo abrir la entidad"); }
  };

  const openLogs = async (conn) => {
    try {
      const { data } = await api.get(`/oracle/connections/${conn.id}/logs`);
      setLogsModal({ conn, logs: data });
    } catch { toast.error("No se pudo cargar la bitácora"); }
  };

  const remove = async (conn) => {
    if (!window.confirm(`¿Eliminar la conexión "${conn.name}" y todos sus datos absorbidos?`)) return;
    try { await api.delete(`/oracle/connections/${conn.id}`); toast.success("Conexión eliminada"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  if (denied) {
    return (
      <OSLayout>
        <div className="max-w-lg mx-auto mt-24 text-center" data-testid="oracle-denied">
          <ShieldCheck className="w-12 h-12 text-violet mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-navy">Acceso restringido</h1>
          <p className="text-navy/60 mt-2 text-sm">Solo el liderazgo o el equipo de TI puede administrar las conexiones a Oracle.</p>
        </div>
      </OSLayout>
    );
  }

  return (
    <OSLayout>
      <div className="max-w-6xl mx-auto pb-24">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-violet text-xs font-medium mb-2"><Database className="w-4 h-4" /> OMNIA ORACLE BRIDGE</div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Conexión con Oracle</h1>
            <p className="text-navy/60 mt-2 text-sm max-w-2xl">Conecta OMNIA a Oracle Fusion, NetSuite o tu base de datos Oracle y absorbe toda la información. El TI del cliente elige el método y carga sus credenciales; OMNIA ingiere y guarda todo de forma segura.</p>
          </div>
          <button data-testid="oracle-new-connection" onClick={() => setEditModal({ new: true })}
            className="rounded-full bg-gradient-violet text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0">
            <Plus className="w-4 h-4" /> Nueva conexión
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard label="Conexiones" value={ov.connections} testid="oracle-kpi-conns" />
          <KpiCard label="Activas" value={ov.active} testid="oracle-kpi-active" />
          <KpiCard label="Registros absorbidos" value={ov.records.toLocaleString("es-MX")} testid="oracle-kpi-records" />
          <KpiCard label="Entidades" value={ov.entities} testid="oracle-kpi-entities" />
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-violet" /></div>
        ) : conns.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy/15 bg-white p-12 text-center" data-testid="oracle-empty">
            <Plug className="w-10 h-10 text-navy/25 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-navy">Aún no hay conexiones a Oracle</h3>
            <p className="text-navy/50 text-sm mt-1">Crea la primera conexión. Puedes dejarla en modo sandbox para probar el flujo sin credenciales reales.</p>
            <button onClick={() => setEditModal({ new: true })} className="mt-5 rounded-full bg-navy text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90">
              <Plus className="w-4 h-4" /> Nueva conexión
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="oracle-connections">
            {conns.map((c) => {
              const M = KIND_META[c.kind] || KIND_META.oracle_db;
              const S = STATUS_META[c.status] || STATUS_META.nueva;
              const busy = busyId === c.id;
              return (
                <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ease }}
                  className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={`oracle-conn-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl ${M.bg} ${M.color} flex items-center justify-center shrink-0`}><M.icon className="w-5 h-5" /></div>
                      <div className="min-w-0">
                        <div className="font-medium text-navy truncate">{c.name}</div>
                        <div className="text-xs text-navy/50 truncate">{c.kind_label}</div>
                      </div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${S.c}`} data-testid={`oracle-status-${c.id}`}>{S.l}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-4 text-xs text-navy/50">
                    <span><b className="text-navy">{(c.records_count || 0).toLocaleString("es-MX")}</b> registros</span>
                    {c.last_sync && <span>Últ. sync: {new Date(c.last_sync).toLocaleString("es-MX")}</span>}
                    {c.sandbox && <span className="text-gold">Sandbox</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button data-testid={`oracle-test-${c.id}`} onClick={() => test(c)} disabled={busy} className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />} Probar
                    </button>
                    <button data-testid={`oracle-discover-${c.id}`} onClick={() => openDiscover(c)} disabled={busy} className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-navy/5 disabled:opacity-50">
                      <Search className="w-3.5 h-3.5" /> Descubrir
                    </button>
                    <button data-testid={`oracle-sync-${c.id}`} onClick={() => runSync(c, [])} disabled={busy} className="text-xs rounded-lg bg-violet text-white px-3 py-1.5 inline-flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sincronizar
                    </button>
                    <button data-testid={`oracle-data-${c.id}`} onClick={() => openData(c)} className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-navy/5">
                      <ListTree className="w-3.5 h-3.5" /> Ver datos
                    </button>
                    <button data-testid={`oracle-logs-${c.id}`} onClick={() => openLogs(c)} className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-navy/5">
                      <ScrollText className="w-3.5 h-3.5" /> Bitácora
                    </button>
                    <button data-testid={`oracle-edit-${c.id}`} onClick={() => setEditModal({ conn: c })} className="text-xs rounded-lg border border-navy/15 px-2.5 py-1.5 hover:bg-navy/5"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`oracle-delete-${c.id}`} onClick={() => remove(c)} className="text-xs rounded-lg border border-rose-200 text-rose-500 px-2.5 py-1.5 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {editModal && <ConnModal kinds={kinds} data={editModal} onClose={() => setEditModal(null)} onSaved={() => { setEditModal(null); load(); }} />}
        {discoverModal && <DiscoverModal state={discoverModal} setState={setDiscoverModal} onSync={runSync} busy={busyId === discoverModal.conn.id} />}
        {dataModal && <DataModal state={dataModal} onClose={() => setDataModal(null)} onPick={(e) => loadTable(dataModal.conn, e, dataModal.datasets)} onUploaded={() => openData(dataModal.conn)} />}
        {logsModal && <LogsModal state={logsModal} onClose={() => setLogsModal(null)} />}
      </AnimatePresence>
    </OSLayout>
  );
}

// ---------- Connection create/edit ----------
function ConnModal({ kinds, data, onClose, onSaved }) {
  const isNew = !!data.new;
  const existing = data.conn;
  const [kind, setKind] = useState(existing?.kind || "fusion");
  const [name, setName] = useState(existing?.name || "");
  const [sandbox, setSandbox] = useState(isNew ? true : !!existing?.sandbox);
  const [cfg, setCfg] = useState(() => {
    const c = { ...(existing?.config || {}) };
    // limpiar los flags "_set" para no mandarlos como valores
    Object.keys(c).forEach((k) => { if (k.endsWith("_set")) delete c[k]; });
    return c;
  });
  const [saving, setSaving] = useState(false);
  const meta = kinds.find((k) => k.kind === kind);
  const secretSet = existing?.config || {};

  const setField = (key, val) => setCfg((c) => ({ ...c, [key]: val }));

  const save = async () => {
    setSaving(true);
    // convertir campos "list" (textarea) a arreglos
    const payloadCfg = { ...cfg };
    (meta?.fields || []).forEach((f) => {
      if (f.type === "list" && typeof payloadCfg[f.key] === "string") {
        payloadCfg[f.key] = payloadCfg[f.key].split("\n").map((s) => s.trim()).filter(Boolean);
      }
    });
    try {
      if (isNew) await api.post("/oracle/connections", { name, kind, config: payloadCfg, sandbox, enabled: true });
      else await api.put(`/oracle/connections/${existing.id}`, { name, kind: existing.kind, config: payloadCfg, sandbox, enabled: true });
      toast.success(isNew ? "Conexión creada" : "Conexión actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-navy">{isNew ? "Nueva conexión a Oracle" : "Editar conexión"}</h2>
        <button onClick={onClose} data-testid="oracle-modal-close"><X className="w-5 h-5 text-navy/40" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-navy/50 block mb-1">Nombre</label>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Oracle Genesis (producción)" data-testid="oracle-field-name" />
        </div>
        {isNew && (
          <div>
            <label className="text-xs text-navy/50 block mb-1">Tipo de conexión</label>
            <select className={input} value={kind} onChange={(e) => { setKind(e.target.value); setCfg({}); }} data-testid="oracle-field-kind">
              {kinds.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
            </select>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {(meta?.fields || []).map((f) => (
            <div key={f.key} className={f.type === "list" || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="text-xs text-navy/50 block mb-1">{f.label}{f.secret && <span className="text-gold ml-1">(secreto)</span>}</label>
              {f.type === "select" ? (
                <select className={input} value={cfg[f.key] ?? f.options[0]} onChange={(e) => setField(f.key, e.target.value)} data-testid={`oracle-cfg-${f.key}`}>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "list" || f.type === "textarea" ? (
                <textarea rows={3} className={input} value={Array.isArray(cfg[f.key]) ? cfg[f.key].join("\n") : (cfg[f.key] || "")}
                  onChange={(e) => setField(f.key, e.target.value)} placeholder={f.placeholder || ""} data-testid={`oracle-cfg-${f.key}`} />
              ) : (
                <input type={f.secret ? "password" : "text"} className={input} value={cfg[f.key] || ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.secret && secretSet[`${f.key}_set`] ? "•••••• (guardado, deja vacío para conservar)" : (f.placeholder || "")}
                  data-testid={`oracle-cfg-${f.key}`} />
              )}
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer">
          <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} data-testid="oracle-field-sandbox" />
          Modo sandbox (datos simulados para probar sin credenciales reales)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:bg-navy/5">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="oracle-save-connection" className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
          </button>
        </div>
      </div>
    </Overlay>
  );
}

// ---------- Discover + sync selection ----------
function DiscoverModal({ state, setState, onSync, busy }) {
  const { conn, objects, selected, sandbox } = state;
  const toggle = (name) => setState((s) => {
    const n = new Set(s.selected); n.has(name) ? n.delete(name) : n.add(name); return { ...s, selected: n };
  });
  return (
    <Overlay onClose={() => setState(null)}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-navy">Objetos en {conn.name}</h2>
          {sandbox && <p className="text-xs text-gold mt-1">Descubrimiento simulado (sandbox)</p>}
        </div>
        <button onClick={() => setState(null)}><X className="w-5 h-5 text-navy/40" /></button>
      </div>
      <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="oracle-discover-list">
        {objects.map((o) => (
          <label key={o.name} className="flex items-center gap-3 p-2.5 rounded-lg border border-navy/10 hover:bg-navy/5 cursor-pointer" data-testid={`oracle-obj-${o.name}`}>
            <input type="checkbox" checked={selected.has(o.name)} onChange={() => toggle(o.name)} />
            <TableProperties className="w-4 h-4 text-violet" />
            <span className="text-sm text-navy flex-1">{o.label || o.name}</span>
            <span className="text-[11px] text-navy/40">{o.type}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <button onClick={() => setState(null)} className="rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:bg-navy/5">Cancelar</button>
        <button data-testid="oracle-sync-selected" onClick={() => onSync(conn, Array.from(selected))} disabled={busy || selected.size === 0}
          className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizar {selected.size} seleccionados
        </button>
      </div>
    </Overlay>
  );
}

// ---------- Data explorer ----------
function DataModal({ state, onClose, onPick, onUploaded }) {
  const { conn, datasets, active, table } = state;
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity", (file.name.split(".")[0] || "records"));
    try { const { data } = await api.post(`/oracle/connections/${conn.id}/upload`, fd); toast.success(`${data.count} registros absorbidos`); onUploaded(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar"); }
    finally { setUploading(false); }
  };
  return (
    <Overlay onClose={onClose} wide>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-navy">Datos absorbidos · {conn.name}</h2>
        <div className="flex items-center gap-2">
          <label className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-navy/5 cursor-pointer" data-testid="oracle-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Cargar CSV/XML
            <input type="file" accept=".csv,.xml" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <button onClick={onClose}><X className="w-5 h-5 text-navy/40" /></button>
        </div>
      </div>
      {datasets.length === 0 ? (
        <p className="text-navy/50 text-sm py-10 text-center">Aún no hay datos. Sincroniza la conexión o carga un archivo.</p>
      ) : (
        <div className="flex gap-4 min-h-[40vh]">
          <div className="w-48 shrink-0 space-y-1" data-testid="oracle-entity-list">
            {datasets.map((d) => (
              <button key={d.entity} onClick={() => onPick(d.entity)} data-testid={`oracle-entity-${d.entity}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm ${active === d.entity ? "bg-violet/10 text-violet" : "hover:bg-navy/5 text-navy/70"}`}>
                <div className="truncate">{d.entity}</div>
                <div className="text-[11px] text-navy/40">{d.count} registros</div>
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-auto border border-navy/10 rounded-xl">
            {table ? (
              <table className="w-full text-xs" data-testid="oracle-data-table">
                <thead className="bg-navy/5 sticky top-0">
                  <tr>{table.columns.map((c) => <th key={c} className="text-left px-3 py-2 font-medium text-navy/60 whitespace-nowrap">{c}</th>)}</tr>
                </thead>
                <tbody>
                  {table.rows.map((r, i) => (
                    <tr key={i} className="border-t border-navy/5">
                      {table.columns.map((c) => <td key={c} className="px-3 py-2 text-navy/80 whitespace-nowrap max-w-xs truncate">{String(r[c] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div>}
          </div>
        </div>
      )}
    </Overlay>
  );
}

// ---------- Logs ----------
function LogsModal({ state, onClose }) {
  const { conn, logs } = state;
  return (
    <Overlay onClose={onClose}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-navy">Bitácora de sincronización · {conn.name}</h2>
        <button onClick={onClose}><X className="w-5 h-5 text-navy/40" /></button>
      </div>
      {logs.length === 0 ? <p className="text-navy/50 text-sm py-10 text-center">Sin sincronizaciones todavía.</p> : (
        <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="oracle-logs-list">
          {logs.map((l) => (
            <div key={l.id} className="rounded-xl border border-navy/10 p-3">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-sm">
                  {l.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-500" />}
                  <b className="text-navy">{l.total}</b> registros · {l.status}{l.sandbox ? " (sandbox)" : ""}
                </span>
                <span className="text-[11px] text-navy/40">{new Date(l.started_at).toLocaleString("es-MX")}</span>
              </div>
              <div className="text-xs text-navy/50 mt-1">
                {(l.entities || []).map((e) => `${e.entity}: ${e.count}`).join("  ·  ")}
                {l.source ? ` · ${l.source}` : ""}
              </div>
              {l.error && <div className="text-xs text-rose-500 mt-1">{l.error}</div>}
            </div>
          ))}
        </div>
      )}
    </Overlay>
  );
}

// ---------- Overlay wrapper ----------
function Overlay({ children, onClose, wide }) {
  return (
    <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }} transition={{ ease }}
        className={`bg-white rounded-2xl p-6 w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        {children}
      </motion.div>
    </motion.div>
  );
}
